import { Component, TemplateRef, ViewEncapsulation, inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { AlbumListComponent } from '../album-list/album-list.component';
import { AlbumActions, VideoAccessContext, VideoRowAccess, accessContextKey, albumActions, rowAccessKey, videoRowAccess } from './video-detail-access';
import { metaParts } from './video-detail-format';
import { Memo, ObjectMemo } from './video-detail-memo';
import { videoDetailThemeKey, videoDetailThemeVars } from './video-detail-theme';
import { InlinePlayback } from './video-detail-inline';
import { PanelTab, commentsEnabledFor, defaultPanelTab, nextPlayableVideo } from './video-detail-panel';
import { VdPlayerComponent } from './vd-player/vd-player.component';
import { albumPictureInfo, videoImage } from './video-detail-thumb';
import { albumShareUrl, applySharePreview, videoShareUrl } from './video-detail-share';
import { warmUpPlayer, warmUpPlayerWhenIdle } from './video-detail-warmup';
import { VdRecsService } from './vd-recs/vd-recs.service';
import { VdCommentsFeedService } from './vd-comments/vd-comments-feed.service';

const DESCRIPTION_PREVIEW_CHARS = 180;

/*
 * New video detail page (video albums). Inherits all data loading, playback, payment,
 * subscription, ads, tips, polls, playlist, share and report logic from AlbumListComponent
 * unchanged; only the layout is new. The classic page stays available as a backup
 * (see core/guards/video-detail-version.guard.ts).
 *
 * The classic stylesheet is included: the shared payment / subscription / tickets panels use its classes.
 */
@Component({
  selector: 'app-video-detail-v2',
  templateUrl: './video-detail-v2.component.html',
  styleUrls: ['../album-list/album-list.component.scss', './video-detail-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [VdRecsService, VdCommentsFeedService],
})
export class VideoDetailV2Component extends AlbumListComponent {
  private readonly meta = inject(Meta);
  readonly recs = inject(VdRecsService);
  readonly commentsFeed = inject(VdCommentsFeedService);

  // Cached per input so change detection (scroll, timers) does not rebuild them or re-render rows.
  private readonly themeMemo = new Memo<Record<string, string>>();
  private readonly contextMemo = new Memo<VideoAccessContext>();
  private readonly actionsMemo = new Memo<AlbumActions>();
  private readonly rowMemo = new ObjectMemo<any, VideoRowAccess>();
  private readonly countdownMemo = new Memo<{ days: number; hours: number; minutes: number; seconds: number }>();

  // Inline playback (Option B): the player replaces the poster after the classic play checks pass.
  readonly inline = new InlinePlayback(video => this.onRowPlay(video));
  readonly warmPlayer = () => warmUpPlayer();
  inlinePlayer: VdPlayerComponent | null = null;
  theatre = false;
  shareTarget: any = null;
  shareAnchor: HTMLElement | null = null;
  panelTabChoice: PanelTab | null = null;

  get commentsEnabled(): boolean { return this.inline.payload ? commentsEnabledFor(this.inline.payload.selectedVideo) : this.commentsFeed.active; }
  get panelTab(): PanelTab { return this.panelTabChoice ?? defaultPanelTab(this.videos.length, this.commentsEnabled); }

  get countdown(): { days: number; hours: number; minutes: number; seconds: number } | null {
    if (!this.showCountDownTimer) return null;
    return this.countdownMemo.get([this.days, this.hours, this.minutes, this.seconds].join('|'),
      () => ({ days: this.days, hours: this.hours, minutes: this.minutes, seconds: this.seconds }));
  }

  isNowPlaying(element: any): boolean { return element?.stream_info?.id === this.inline.currentId; }

  /** After the classic checks (sign-in, age, payment, start session): play here instead of the album-video page. */
  openVideoPlayer(payload: any, slug: string, params: any): void {
    if (payload?.tag_category !== 'video') {
      super.openVideoPlayer(payload, slug, params);
      return;
    }
    // The classic flag closes a play modal on the next navigation; there is no modal on this page.
    this.isPlayVideo = false;
    this.inline.start(payload);
    this.commentsFeed.remember(this.streamInfo?.id);
    const id = payload?.selectedVideo?.id;
    if (id) {
      const url = this.router.parseUrl(this.router.url);
      url.queryParams = { ...url.queryParams, id };
      this.router.navigateByUrl(url, { replaceUrl: true });
    }
  }

  /** The video that plays after the current one (the "Next" tag and the up-next countdown). */
  get nextVideo(): any { return nextPlayableVideo(this.videos, this.inline.currentId, video => this.rowAccess(video).canPlay); }
  onPlayerEnded(): void { this.inline.queueUpNext(this.nextVideo); }

  ngOnDestroy(): void {
    this.inline.cancelUpNext();
    super.ngOnDestroy();
  }

  /** Album data has arrived (until then the page shows loading placeholders). */
  get isLoaded(): boolean { return !!this.streamInfo?.id; }

  get networkName(): string {
    return this.settingsData?.channelInfo?.channel_display_name || '';
  }

  get description(): string {
    return this.streamInfo?.stream_video_description || this.streamInfo?.short_description || '';
  }

  get descriptionIsLong(): boolean {
    return this.description.replace(/<[^>]*>/g, '').length > DESCRIPTION_PREVIEW_CHARS;
  }

  get themeVars(): Record<string, string> {
    return this.themeMemo.get(videoDetailThemeKey(this.settingsData), () => videoDetailThemeVars(this.settingsData));
  }

  get isPreviewUrl(): boolean {
    return this.router.url.includes('/preview');
  }

  get accessContext(): VideoAccessContext {
    const ctx: VideoAccessContext = {
      streamInfo: this.streamInfo,
      isPreview: this.isPreviewUrl,
      showCountDownTimer: this.showCountDownTimer,
      showAlbumPaymentButton: this.showAlbumPaymentButton,
      showAlbumSubscriptionButton: this.showAlbumSubscriptionButton,
      subscribedUserPlans: this.subscribedUserPlans,
      isLoggedIn: !!this.userInfo?.access_token,
    };
    return this.contextMemo.get(accessContextKey(ctx), () => ctx);
  }

  get actions(): AlbumActions {
    const ctx = this.accessContext;
    const key = [accessContextKey(ctx), (this.subEvents || []).length, this.subEvents?.[0]?.stream_info?.polls?.length,
      this.tag_category, this.enablePlay, this.performer_details?.tip_donation, this.isSubscribed, !!this.settingsData?.is_preview].join('|');
    return this.actionsMemo.get(key, () => albumActions(ctx, {
      subEvents: this.subEvents,
      tagCategory: this.tag_category,
      enablePlay: this.enablePlay,
      performerDetails: this.performer_details,
      isSubscribed: this.isSubscribed,
      settingsPreview: !!this.settingsData?.is_preview,
    }));
  }

  get videos(): any[] {
    return (this.dataSourceVideo?.data as any[]) || [];
  }

  /** Album picture: its own image, else the first video's image or a frame from that video. */
  get heroPictureInfo(): any {
    return albumPictureInfo(this.streamInfo, this.videos[0]?.stream_info);
  }

  /** After the album data loads (classic code), set the link-preview tags for shares. */
  getStreamDetails(response: any, type?: any): void {
    super.getStreamDetails(response, type);
    // Signed-in viewers can play right away: fetch the player scripts while idle (guests: on pointer over Play).
    if (this.tag_category === 'video' && this.userInfo?.access_token) warmUpPlayerWhenIdle();
    applySharePreview(this.meta, { title: this.streamInfo?.stream_video_title, image: videoImage(this.heroPictureInfo),
      description: this.description.replace(/<[^>]*>/g, '').slice(0, 200), url: albumShareUrl(environment.CONSUMER_PORTAL, this.router.url) });
    this.recs.load({ album: type == 'preview' ? response?.Data : response?.data?.Data, currentId: this.streamInfo?.id, channelId: this.settingsData?.channelInfo?.channel_id,
      performerId: this.performerId, accessToken: this.userInfo?.access_token, countryCode: this.currentLocation?.country_code2 || '', fallbackImage: this.video_banner_image || '' });
    this.commentsFeed.load(this.streamInfo?.id, this.subEvents?.[0]?.stream_info, this.isPreviewUrl);
  }

  get metaLine(): string[] { return metaParts(this.streamInfo); }

  rowAccess(element: any): VideoRowAccess {
    const ctx = this.accessContext;
    return this.rowMemo.get(element, accessContextKey(ctx) + '#' + rowAccessKey(element), () => videoRowAccess(element, ctx));
  }

  /** Share sheet for one video; shareVideo() keeps the classic share analytics event. */
  openShare(element: any, anchor?: HTMLElement): void { this.shareVideo(element); this.shareTarget = element; this.shareAnchor = anchor || null; }
  closeShare(): void { this.closeSocialSharePopup(true); this.shareTarget = null; }
  get shareUrl(): string { return videoShareUrl(environment.CONSUMER_PORTAL, this.router.url, this.shareTarget?.stream_info?.id); }
  get shareImage(): string | null { return videoImage(this.shareTarget?.stream_info); }

  /** Spinner on the clicked Play while the classic checks and start-session call run. */
  openModalWithClass(template: TemplateRef<any>, ele?: any, doCall?: boolean): Promise<void> {
    return this.inline.track(ele?.stream_info?.id, () => super.openModalWithClass(template, ele, doCall));
  }

  onHeroPlay(): void {
    const actions = this.actions;
    if (actions.showPlay && !actions.playDisabled && !this.inline.pendingId) this.playFromBanner(this.template);
  }

  onRowPlay(element: any): void {
    if (!this.inline.pendingId) this.openModalWithClass(this.template, element);
  }

  onRowLike(element: any, index: number): void {
    if (this.userInfo?.access_token) this.likeAudioVideo(element, index); else this.requireLogin();
  }

  trackByVideo(index: number, element: any): any {
    return element?.stream_info?.id ?? index;
  }
}
